/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 16 2024
 **/

import { ModalPopup, Typography, useModal } from "@/frameworks/designStuff/components";
import { useStompClient } from "@/frameworks/libraries/network";
import { _T } from "@rfice/language";
import { Properties } from "@/frameworks/libraries/properties";
import { SceneLevel, SceneType } from "@/frameworks/libraries/commons";

import { ModalID } from "@/frameworks/core/commons";
import { useConfirm, useFriendHandlers, useGuestSignupConfirm } from "@/frameworks/core/hooks";
import { useUnitySystemActions } from "@/frameworks/core/unity";
import { useSpace } from "@/frameworks/core/API";

const DEFAULT_SPACE = {
	spaceId: Properties.property.user?.myRoomId || "",
	spaceScene: {
		path: "",
		sceneLevel: SceneLevel.MyRoom,
		version: "",
		sceneType: {
			type: "",
			typeNumber: SceneType.MyRoomScene
		}
	}
};

export default function MessagePopupProvider() {
	const modal = useModal();
	const { confirm } = useConfirm();
	const guestSignupConfirm = useGuestSignupConfirm();
	const { handlers } = useFriendHandlers();
	const { go } = useUnitySystemActions();
	const spaceAPI = useSpace();
	const isGuest = Properties.property.isGuest;

	useStompClient(
		{
			onMessageReceived(msg) {
				switch (msg.type) {
					case "spaceThemeUpdated": {
						const payload = msg.payload as PushServices.Space.SpaceThemeUpdated.Data;
						confirm(
							{
								title: _T("스페이스 변경 안내"),
								descriptions: _T("스페이스 정보가 변경되어 스페이스에 재접속되었습니다."),
								useOnlyConfirm: true
							},
							{
								async onConfirm() {
									const { data } = await spaceAPI.detail.execute({ spaceId: payload.spaceInfo.id });
									if (data) {
										go("userGuesture", {
											spaceId: payload.spaceInfo.id,
											spaceScene: data.spaceScene,
											spaceType: "SPACE"
										});
									}
								}
							}
						);
						break;
					}
					case "groupSpaceUnavailable": {
						const defaultSpace = Properties.property?.user?.entrySpace ?? DEFAULT_SPACE;
						confirm(
							{
								title: _T("그룹/스페이스 조건에 부합하지 않아 그룹/스페이스에 참여하실 수 없습니다."),
								useOnlyConfirm: true
							},
							{
								onConfirm() {
									go("userGuesture", defaultSpace);
								}
							}
						);
						break;
					}
					case "requestSummonFriend":
						{
							modal.hide(ModalID.CONFIRM_REQUEST_SUMMON_FRIEND);

							const payload = msg.payload as ChatServices.C2C.RequestSummonFriend.Data;
							const { requesterID, requesterName } = payload;
							modal.show({
								id: ModalID.CONFIRM_REQUEST_SUMMON_FRIEND,
								content: (
									<ModalPopup
										modalID={ModalID.CONFIRM_REQUEST_SUMMON_FRIEND}
										title={_T("불러오기")}
										confirmText={_T("수락하기")}
										cancelText={_T("취소")}
										onConfirm={(_, hide) => {
											if (isGuest) {
												guestSignupConfirm(() => {});
											} else {
												handlers.handleFollowUser(requesterID);
												hide();
											}
										}}
										fitContent
									>
										<Typography.p typoSize="Title2">
											{_T("{{user}}님쪽으로 오길 요청합니다.", { user: requesterName })}
										</Typography.p>
										<Typography.p typoSize="Title2">{_T("요청을 수락 하시겠습니까?")}</Typography.p>
									</ModalPopup>
								)
							});
						}
						break;
				}
			}
		},
		[isGuest]
	);
	return null;
}
